import { infiniteScrollFormBase, type InfiniteScrollForm } from "./infinite-scroll-form";

export class InfiniteScrollRender {
    public form: InfiniteScrollForm;

    public constructor(form: InfiniteScrollForm) {
        this.form = form;
    }

    public ensureSentinel(): void {
        if (this.form.getSentinelElement() instanceof HTMLElement) return;

        const sentinelHtml = [
            '<div class="infinite-scroll-sentinel" aria-hidden="true">',
            '<span class="spinner spinner--inline-load infinite-scroll-sentinel__spinner" role="status" aria-live="polite" hidden></span>',
            "</div>",
        ].join("");

        const pagination = this.form.getPaginationElement();
        const list = this.form.getListElement();

        if (pagination && pagination.parentNode) {
            pagination.insertAdjacentHTML("afterend", sentinelHtml);
            const sentinel = this.pickLastSentinel(pagination.parentElement);
            const spinner = sentinel ? infiniteScrollFormBase.getSpinner(sentinel) : null;
            this.form.setSentinelElement(sentinel);
            this.form.setIndicatorElement(spinner instanceof HTMLElement ? spinner : null);
            return;
        }

        if (list && list.parentNode) {
            list.insertAdjacentHTML("afterend", sentinelHtml);
            const sentinel = this.pickLastSentinel(list.parentElement);
            const spinner = sentinel ? infiniteScrollFormBase.getSpinner(sentinel) : null;
            this.form.setSentinelElement(sentinel);
            this.form.setIndicatorElement(spinner instanceof HTMLElement ? spinner : null);
        }
    }

    public setPaginationHidden(hidden: boolean): void {
        const pagination = this.form.getPaginationElement();
        if (!(pagination instanceof HTMLElement)) return;
        pagination.hidden = hidden;
    }

    public setIndicatorVisible(visible: boolean): void {
        const indicator = this.form.getIndicatorElement();
        if (!(indicator instanceof HTMLElement)) return;
        indicator.hidden = !visible;
    }

    public hideSentinel(): void {
        const sentinel = this.form.getSentinelElement();
        if (!(sentinel instanceof HTMLElement)) return;
        sentinel.hidden = true;
        this.setIndicatorVisible(false);
    }

    public showSentinel(): void {
        const sentinel = this.form.getSentinelElement();
        if (!(sentinel instanceof HTMLElement)) return;
        sentinel.hidden = false;
    }

    public appendItemsFromDocument(doc: Document): number {
        const list = this.form.getListElement();
        if (!(list instanceof HTMLElement)) return 0;

        const sourceList = infiniteScrollFormBase.getListElement(doc, this.form.getListSelector());
        if (!(sourceList instanceof HTMLElement)) return 0;

        const nodes = infiniteScrollFormBase.getItems(sourceList, this.form.getItemSelector());
        if (!nodes.length) return 0;

        const html = nodes.map((node) => node.outerHTML).join("");

        if (!html) return 0;
        list.insertAdjacentHTML("beforeend", html);
        return nodes.length;
    }

    public dispatchContentInserted(): void {
        const list = this.form.getListElement();
        if (!(list instanceof HTMLElement)) return;

        document.dispatchEvent(
            new CustomEvent("app:content:inserted", {
                detail: { root: list },
            }),
        );
    }

    private pickLastSentinel(container: HTMLElement | null): HTMLElement | null {
        if (!(container instanceof HTMLElement)) return null;
        const sentinels = infiniteScrollFormBase.getSentinels(container);
        if (!sentinels.length) return null;
        const last = sentinels[sentinels.length - 1];
        return last instanceof HTMLElement ? last : null;
    }
}
